import { faCcMastercard, faCcVisa } from "@fortawesome/free-brands-svg-icons";

import { Action } from "redux";
import { IconDefinition } from "@fortawesome/free-solid-svg-icons";
import { ThunkResult } from '.';

const seedrandom = require('seedrandom')

interface AccountsState {
  loading: boolean
  loaded: boolean
  error: Error | null
  list: Account[]
  statistics: AccountStatistics
  transactions: AccountTransaction[]
}

const initialState: AccountsState = {
  loading: false,
  loaded: false,
  error: null,
  list: [] as Account[],
  statistics: {
    spending: {
      categories: [],
      amount: []
    },
    historical: {
      out: [],
      assets: [],
    }
  },
  transactions: []
}

interface AccountStatistics {
  spending: {
    categories: string[]
    amount: number[]
  }
  historical: {
    out: number[]
    assets: number[]
  }
}

interface AccountTransaction {
  amount: number
  description: string
  dateOffset: number
}

export const LOADACCOUNTS_BEGIN = 'accounts/LOADACCOUNTS_BEGIN'
export const LOADACCOUNTS_SUCCESS = 'accounts/LOADACCOUNTS_SUCCESS'
export const LOADACCOUNTS_FAILURE = 'accounts/LOADACCOUNTS_FAILURE'
export const LOADACCOUNTS_STATISTICS = 'accoutns/LOADACCOUNTS_STATISTICS'
export const LOADACCOUNTS_TRANSACTIONS = 'accoutns/LOADACCOUNTS_TRANSACTIONS'

interface LoadAccountsBeginAction extends Action {
  type: typeof LOADACCOUNTS_BEGIN,
}

interface LoadAccountsSuccessAction extends Action {
  type: typeof LOADACCOUNTS_SUCCESS,
  list: Account[]
}

interface LoadAccountsFailureAction extends Action {
  type: typeof LOADACCOUNTS_FAILURE,
  error: Error
}

interface LoadAccountsStatistics extends Action {
  type: typeof LOADACCOUNTS_STATISTICS,
  statistics: AccountStatistics
}

interface LoadAccountsTransactions extends Action {
  type: typeof LOADACCOUNTS_TRANSACTIONS,
  transactions: AccountTransaction[]
}

export interface Account {
  id: string
  type: string
  name: string
  balance: number
  currency: string
  balanceFn: (rng: { quick(): number }) => number
  icon: IconDefinition | null
}

const randomBalance = (base: number, variant: number) => (rng: any) => {
  let balance = base + rng.quick() * variant
  balance = Math.round(balance * 100) / 100
  return balance
}

const accounts = [
  {
    id: "2564454541-1",
    type: "deposit",
    name: "Checking Account",
    balance: 0,
    balanceFn: randomBalance(1000, 2000),
    currency: "USD",
  },
  {
    id: "2564454541-2",
    type: "deposit",
    name: "Savings Account",
    balance: 0,
    balanceFn: randomBalance(22000, 4000),
    currency: "USD"
  },
  {
    id: "4003-5948-5743-1382",
    type: "card",
    name: "Visa Infinite",
    balance: 0,
    balanceFn: randomBalance(-1000, -500),
    currency: "USD",
    icon: faCcVisa
  },
  {
    id: "XXXX-XXXX-XXXX-XXXX",
    name: "MasterCard Platinum",
    type: "card",
    balance: 0,
    balanceFn: randomBalance(-800, -300),
    currency: "USD",
    icon: faCcMastercard
  },
  {
    id: "XXXX-XXXX-XXXX-XXX1",
    name: "Securities Trading",
    type: "investment",
    balance: 0,
    balanceFn: randomBalance(1000, 4000),
    currency: "USD"
  },
  {
    id: "XXXX-XXXX-XXXX-2",
    type: "investment",
    name: "Securities Trading (GBP)",
    balance: 0,
    balanceFn: randomBalance(4000, 6000),
    currency: "GBP"
  },
  {
    id: "XXXX-XXXX-XXXX-XXX3",
    type: "investment",
    name: "Securities Trading (EUR)",
    balance: 0,
    balanceFn: randomBalance(6000, 10000),
    currency: "EUR"
  }
] as Account[]

export const loadAccounts = (): ThunkResult<Promise<LoadAccountsSuccessAction | LoadAccountsFailureAction>> =>
  (dispatch, getState) => {
    dispatch({
      type: LOADACCOUNTS_BEGIN,
    } as LoadAccountsBeginAction)

    return new Promise<LoadAccountsSuccessAction | LoadAccountsFailureAction>((resolve, reject) => {
      const rng = seedrandom(getState().auth.userName)
      let list = accounts.map(a => {
        a.balance = a.balanceFn(rng)
        return a
      })

      resolve({
        type: LOADACCOUNTS_SUCCESS,
        list
      } as LoadAccountsSuccessAction)
    }).then(action => {
      return dispatch(action)
    }).then(action => {
      dispatch(loadAccountStatistics())
      return action
    }).then(action => {
      dispatch(loadAccountTransactions())
      return action
    })
  }

const loadAccountStatistics = (): ThunkResult<LoadAccountsStatistics> =>
  (dispatch, getState) => {
    let statistics = {
      historical: {
        out: [],
        assets: []
      },
      spending: {
        categories: ["Auto & Transport", "Dining", "Entertainment", "Groceries", "Rent, Mortgage & Home", "Insurance & Investment", "Medical & Personal Care", "Shopping", "Utilities & Telecoms", "Miscellaneous"],
        amount: []
      }
    } as AccountStatistics

    const months = 6
    const state = getState()
    let rng = seedrandom(state.auth.userName + "out")

    for (let i = 0; i < months; i++) {
      let out = rng.quick() * 4000 + 2000
      out = Math.round(out * 100) / 100
      statistics.historical.out.unshift(out)
    }

    rng = seedrandom(state.auth.userName + "assets")
    for (let i = 0; i < 6; i++) {
      statistics.historical.assets = statistics.historical.out.map(a => {
        a = 5500 - a
        a = Math.round(a * 100) / 100
        return a
      })
    }

    let latestSpending = _.last(statistics.historical.out) || 4000
    let spendingPct: number[] = []
    let spendingTotal: number = 0

    rng = seedrandom(state.auth.userName + "spending")
    for (let i = 0; i < statistics.spending.categories.length; i++) {
      let spending = rng.quick()
      spendingPct.push(spending)
      spendingTotal += spending
    }
    let amounts = spendingPct.map(spending => {
      let catSpending = spending / spendingTotal * latestSpending
      catSpending = Math.round(catSpending * 100) / 100
      return catSpending
    })

    statistics.spending.amount = amounts

    return dispatch({
      type: LOADACCOUNTS_STATISTICS,
      statistics,
    } as LoadAccountsStatistics)
  }

const loadAccountTransactions = (): ThunkResult<LoadAccountsTransactions> =>
  (dispatch, getState) => {
    let rng = seedrandom(getState().auth.userName + "transactions")

    let transactions = [{
      dateOffset: 0,
      description: "ATM Withdrawal",
      amount: -1 * (80 + Math.round(rng.quick() * 10) * 10)
    },
    {
      dateOffset: -1,
      description: "Bill Payment",
      amount: -1 * Math.round((rng.quick() * 150 + 150) / 100) * 100
    },
    {
      dateOffset: -2,
      description: "Salary Credit",
      amount: (6000 + Math.round(rng.quick() * 30) * 100)
    },
    {
      dateOffset: -5,
      description: "Fund Transfer",
      amount: -1 * Math.round((rng.quick() * 400 + 1000) / 100) * 100
    },
    {
      dateOffset: -5,
      description: "ATM Withdrawal",
      amount: -1 * (130 + Math.round(rng.quick() * 10) * 10)
    }]
    return dispatch({
      type: LOADACCOUNTS_TRANSACTIONS,
      transactions
    } as LoadAccountsTransactions)
  }

export default (state = initialState, action: Action) => {
  switch (action.type) {
    case LOADACCOUNTS_BEGIN:
      return {
        ...state,
        loading: true,
      } as AccountsState
    case LOADACCOUNTS_SUCCESS:
      return {
        ...state,
        loading: false,
        loaded: true,
        list: (action as LoadAccountsSuccessAction).list,
      } as AccountsState
    case LOADACCOUNTS_FAILURE:
      return {
        ...state,
        loading: false,
        loaded: true,
        error: (action as LoadAccountsFailureAction).error
      } as AccountsState
    case LOADACCOUNTS_STATISTICS:
      return {
        ...state,
        statistics: (action as LoadAccountsStatistics).statistics
      } as AccountsState
    case LOADACCOUNTS_TRANSACTIONS:
      return {
        ...state,
        transactions: (action as LoadAccountsTransactions).transactions
      } as AccountsState
    default:
      return state
  }
}